import { makeRequestWithRetry } from 'src/utils/graphql';
import _ from 'lodash';
import {
  ProfessionalsDownloadsProductsActions,
  ProfessionalsDownloadsProductFiltersType,
  ProfessionalsDownloadsProductPaginationType,
  ProfessionalsDownloadsProductSortOrderType,
} from './types';
import {
  QUERY_PROFESSIONALS_DOWNLOADS_PRODUCTS_FILTER_DATA,
  QUERY_PROFESSIONALS_DOWNLOADS_PRODUCTS_SEARCH,
  QUERY_PROFESSIONALS_DOWNLOADS_PRODUCT_DETAILS,
  QUERY_PROFESSIONALS_DOWNLOADS_PATHS,
} from '../queries';
import { getOutputChannel } from 'src/modules/locale/components/Provider/outputChannel';

import { ProductCategoryEntity, ProductEntity } from 'src/modules/products';
import { LocationTypeEntity } from 'src/modules/locationTypes';

const PAGE_SIZE = 100;

export const fetchProfessionalsDownloadsProductsData = async () => {
  const doRequest = async (offset: number, limit: number) => {
    return await makeRequestWithRetry<{
      productsSearch: ProductEntity[];
      searchResultsCount: number;
    }>(
      QUERY_PROFESSIONALS_DOWNLOADS_PRODUCTS_SEARCH,
      {
        term: '',
        categoryIds: [],
        locationTypeIds: [],
        locale: 'en',
        offset,
        limit,
        sortColumn: 'name',
        sortDirection: 'asc',
        outputChannel: '',
      },
      { errorId: 'fetchProfessionalsDownloadsProductsData()' }
    );
  };

  const pageResults = [await doRequest(0, PAGE_SIZE)];
  const resultsCount = pageResults[0].searchResultsCount;
  const paginationTimes = Math.ceil((resultsCount - PAGE_SIZE) / PAGE_SIZE);

  await Promise.all(
    _.times(paginationTimes, (_i) =>
      (async () => {
        const i = _i + 1;
        pageResults[i] = await doRequest(i * PAGE_SIZE, PAGE_SIZE);
      })()
    )
  );

  const results = { productsSearch: [], searchResultsCount: resultsCount };
  _.each(pageResults, (r) => {
    results.productsSearch = results.productsSearch.concat(r.productsSearch);
  });

  return results;
};

export const fetchFilterData = async () => {
  const response = await makeRequestWithRetry<{
    productCategories: ProductCategoryEntity[];
    locationTypes: LocationTypeEntity[];
  }>(QUERY_PROFESSIONALS_DOWNLOADS_PRODUCTS_FILTER_DATA, {}, { errorId: 'fetchFilterData()' });

  return response;
};

export const fetchDetails = async (slug: string) => {
  const outputChannel = getOutputChannel();
  const response = await makeRequestWithRetry<{ products: ProductEntity[] }>(
    QUERY_PROFESSIONALS_DOWNLOADS_PRODUCT_DETAILS,
    { slug, outputChannel, mediaOutputChannel: `${outputChannel}_media` },
    { errorId: 'fetchDetails()' }
  );

  return response;
};

export const fetchPaths = async () => {
  const doRequest = async (offset: number, limit: number) => {
    return await makeRequestWithRetry<{
      productsSearch: { slug: string }[];
      searchResultsCount: number;
    }>(
      QUERY_PROFESSIONALS_DOWNLOADS_PATHS,
      {
        offset,
        limit,
      },
      { errorId: 'fetchPaths()' }
    );
  };

  const pageResults = [await doRequest(0, PAGE_SIZE)];
  const resultsCount = pageResults[0].searchResultsCount;
  const paginationTimes = Math.ceil((resultsCount - PAGE_SIZE) / PAGE_SIZE);

  await Promise.all(
    _.times(paginationTimes, (_i) =>
      (async () => {
        const i = _i + 1;
        pageResults[i] = await doRequest(i * PAGE_SIZE, PAGE_SIZE);
      })()
    )
  );

  const results = { productsSearch: [], searchResultsCount: resultsCount };
  _.each(pageResults, (r) => {
    results.productsSearch = results.productsSearch.concat(r.productsSearch);
  });

  return results;
};

export interface SearchProductsArguments {
  filters: ProfessionalsDownloadsProductFiltersType;
  pagination: ProfessionalsDownloadsProductPaginationType;
  sortOrder: ProfessionalsDownloadsProductSortOrderType;
  outputChannel: string;
}

export const searchProducts = ({ filters }: SearchProductsArguments) => {
  return {
    type: ProfessionalsDownloadsProductsActions.SearchSuccess,
    filters,
  };
};

export const setAllProducts = (results: ProductEntity[], searchResultsCount: number) => {
  return {
    type: ProfessionalsDownloadsProductsActions.SearchSuccess,
    payload: results,
    searchResultsCount,
  };
};

export const searchSuggestions = ({ term = '' }: ProfessionalsDownloadsProductFiltersType) => {
  return {
    type: ProfessionalsDownloadsProductsActions.SearchSuggestionsSuccess,
    filters: { term },
  };
};
